import type { ModelKind } from './ModelPicker'
import type { Availability } from '../api'
import { nnTileUrl, sharedTileUrl, upwellingTileUrl, upwMechTileUrl } from '../map/tileUrl'
import { readinessAction } from '../map/readiness'
import { DEFAULT_RASTER, LEGACY } from './upwellingSelection'
import { NN } from './hsiSelection'

/** Слой, которым закрашен фон раздела.
 *
 *  У апвеллинга это ВЫБРАННЫЙ МЕХАНИЗМ (`upw_*`), а не имя раздела: заливку
 *  задаёт радиогруппа «Чем закрасить». Замороженный индекс остаётся в семье
 *  под своим прежним ключом `upwelling` — под ним 2,9 ГБ уже отрисованных
 *  тайлов, и переезд осиротил бы их.
 */
export type BackgroundLayer = 'currents' | 'upwelling' | 'nn' | `upw_${string}`

/** Принадлежит ли ключ семье апвеллинга.
 *
 *  Проверка нужна потому, что подраздел раздела приезжает сюда ОДНОЙ
 *  переменной на обе семьи: без неё ключ подраздела HSI, оставшийся в ней,
 *  стал бы фоном апвеллинга — и адрес собрался бы для слоя другого раздела.
 */
function isUpwLayer(id?: string): boolean {
  return !!id && (id === LEGACY || id.startsWith('upw_'))
}

/** Фоновый слой раздела — тот, что показывается сразу, без кнопки «Загрузить».
 *
 *  Течения: снос косяков читается только поверх самого поля течений.
 *  Апвеллинг: безвидовой индекс, считается вместе с прогнозом — если он за дату
 *  уже посчитан, требовать кнопку значит требовать ПЕРЕСЧЁТ готового.
 *  HSI и сезонный прогноз зависят от вида и считаются по запросу — им фона нет.
 */
export function backgroundLayerFor(
  model: ModelKind,
  /** Выбранный подраздел раздела: механизм у апвеллинга, ключ слоя у HSI. */
  sub?: string,
): BackgroundLayer | null {
  if (model === 'currents') return 'currents'
  // Раздел не красит «апвеллинг вообще» — он красит выбранный механизм.
  // До приезда каталога это ЭТАЛОН, а не замороженный индекс: оставив лицом
  // раздела то, что океанолог назвал ошибочным, мы вернули бы прежний вид.
  if (model === 'upwelling') {
    return (isUpwLayer(sub) ? sub : DEFAULT_RASTER) as BackgroundLayer
  }
  // «Комфортная среда»: фон есть ТОЛЬКО у нейрокарты.
  //
  // У нынешнего индекса фонового слоя нет и не заводится — растр показывается
  // через `rasterStatus` + `probabilityAutoShow`, и переезд этой дороги
  // расползся бы на `shouldRepointProbabilityLayer`, автопоказ, `rasterUrlFor`,
  // `probeDay`, `seriesReadiness.probeFor` и ветку `needsSpecies`.
  //
  // Нейрополе, в отличие от него, лежит готовым артефактом и отвечает ОБЩЕЙ
  // ручкой готовности — значит ему место в фоновой машинерии, где даром
  // работают `backgroundDecision`, `pollTilesReady` и `probeDay`.
  if (model === 'hsi') return sub === NN ? NN : null
  return null
}

/** URL тайлов фонового слоя — ЕДИНСТВЕННОЕ место, где он собирается.
 *
 *  Течения — shared-пирамида с глубиной; замороженный индекс — свой ленивый
 *  эндпоинт (глубины у него нет); механизм семьи — свой, с горизонтом.
 *
 *  **Ноль у механизма значит «горизонта нет», а не «поверхность».** Горизонтов
 *  у полей четыре (10/20/30/50), нуля среди них нет вовсе: вертикальный ток на
 *  поверхности стремится к нулю по условию, и поля там не считается.
 */
export function backgroundTileUrl(
  /** Любой растровый слой каталога, а не только фон раздела: этой же дорогой
   *  собирает адреса серия, а она проходит по всему каталогу. `BackgroundLayer`
   *  остаётся у `backgroundLayerFor` — там союз описывает выбор раздела. */
  layer: string,
  date: string,
  depth: number,
  version?: string | null,
  palette?: string | null,
  /** Вид — нужен слоям, считающимся ПО ВИДУ. Сегодня такой один (`nn`), и он же
   *  первый фоновый слой с видом вообще: до него фон был безвидовым целиком.
   *  Вида нет — адреса нет: пустой номер в пути дал бы 404 на каждый тайл. */
  species?: number | null,
): string | null {
  if (layer === LEGACY) return upwellingTileUrl(date, version)
  if (layer.startsWith('upw_')) {
    return upwMechTileUrl(layer, date, depth > 0 ? depth : null, version)
  }
  if (layer === NN) {
    return species == null ? null : nnTileUrl(species, date, version)
  }
  return sharedTileUrl(date, layer, depth, version, palette)
}

export type BgDecision = 'skip' | 'refresh' | 'clear'

/** Что сделать с фоновым слоем, дождавшись availability.
 *
 *  `shownUrl` — что показали оптимистично, `onMapUrl` — что на карте сейчас,
 *  `versionedUrl` — тот же слой с известной версией тайлов.
 *
 *  Ответ может прийти позже, чем пользователь нажал «Загрузить» или сменил
 *  раздел: тогда слой на карте уже другой (или снят), и трогать его нельзя —
 *  иначе поздний ответ воскресит картинку до-пересчётной версии. */
export function backgroundDecision(args: {
  av: Availability
  shownUrl: string
  onMapUrl: string | null
  versionedUrl: string
}): BgDecision {
  if (args.onMapUrl !== args.shownUrl) return 'skip'
  if (readinessAction(args.av) !== 'show-colored') return 'clear'
  return args.versionedUrl === args.shownUrl ? 'skip' : 'refresh'
}
